import { useAuth } from '@clerk/clerk-react';
import { useCallback, useEffect, useRef, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/**
 * phase2-interfaces.md §2.3 — kickoff SSE consumer.
 *
 * Native EventSource cannot attach an Authorization header, so this hook
 * uses fetch + ReadableStream parsing, mirroring `useInterviewStream`.
 * The endpoint emits exactly one event (`turn` on success, `error` on
 * failure / 30s timeout) and then closes — there's no per-token streaming
 * for kickoff.
 *
 * Owner per the contract is S-0b (the worker that publishes the Redis
 * `kickoff:ready:<sessionId>` channel). Sprint 0c implements the consumer
 * side. When 0b's worker isn't deployed, the row sits at `kickoff_status =
 * 'pending'` and this hook surfaces a `timeout` error after 30s.
 */
export type KickoffEvent =
  | { kind: 'turn'; text: string; createdAt: string }
  | { kind: 'error'; code: string; message: string };

export interface UseKickoffSseOptions {
  sessionId: string | null;
  targetUserId?: string | null;
  /** Caller flips this on once the session row is known to need a kickoff. */
  enabled: boolean;
  onEvent: (event: KickoffEvent) => void;
}

interface ParsedEvent {
  event: string;
  data: string;
}

function parseSseBlock(block: string): ParsedEvent | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of block.split('\n')) {
    if (!line || line.startsWith(':')) continue;
    const colonIdx = line.indexOf(':');
    const field = colonIdx === -1 ? line : line.slice(0, colonIdx);
    const value = colonIdx === -1 ? '' : line.slice(colonIdx + 1).replace(/^ /, '');
    if (field === 'event') event = value;
    else if (field === 'data') dataLines.push(value);
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join('\n') };
}

export function useKickoffSse(options: UseKickoffSseOptions): {
  isWaiting: boolean;
  error: string | null;
} {
  const { sessionId, targetUserId, enabled, onEvent } = options;
  const { getToken } = useAuth();

  const [isWaiting, setIsWaiting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onEventRef = useRef(onEvent);
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const startSubscription = useCallback(
    async (id: string, controller: AbortController) => {
      setIsWaiting(true);
      setError(null);
      try {
        const token = await getToken();
        const qs = targetUserId ? `?targetUserId=${targetUserId}` : '';
        const response = await fetch(`${API_URL}/api/v1/interviews/${id}/kickoff${qs}`, {
          method: 'GET',
          signal: controller.signal,
          headers: {
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok || !response.body) {
          const message = `Kickoff request failed (${response.status})`;
          setError(message);
          onEventRef.current({ kind: 'error', code: 'kickoff_failed', message });
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        while (true) {
          const { value, done } = await reader.read();
          if (done) {
            buffer += decoder.decode().replace(/\r\n/g, '\n');
            break;
          }
          buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');

          let sepIdx: number;
          while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
            const rawEvent = buffer.slice(0, sepIdx);
            buffer = buffer.slice(sepIdx + 2);
            const parsed = parseSseBlock(rawEvent);
            if (!parsed) continue;
            try {
              const payload = JSON.parse(parsed.data);
              if (parsed.event === 'turn' && typeof payload.text === 'string') {
                onEventRef.current({
                  kind: 'turn',
                  text: payload.text,
                  createdAt: payload.createdAt ?? new Date().toISOString(),
                });
              } else if (parsed.event === 'error') {
                const code = typeof payload.code === 'string' ? payload.code : 'kickoff_failed';
                const msg =
                  typeof payload.message === 'string' ? payload.message : 'Kickoff failed';
                setError(msg);
                onEventRef.current({ kind: 'error', code, message: msg });
              }
            } catch {
              // Ignore malformed events; the stream may still recover.
            }
          }
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        const message = err instanceof Error ? err.message : 'Kickoff stream error';
        setError(message);
        onEventRef.current({ kind: 'error', code: 'kickoff_failed', message });
      } finally {
        setIsWaiting(false);
      }
    },
    [getToken, targetUserId]
  );

  useEffect(() => {
    if (!enabled || !sessionId) return;
    const controller = new AbortController();
    void startSubscription(sessionId, controller);
    return () => controller.abort();
  }, [enabled, sessionId, startSubscription]);

  return { isWaiting, error };
}
